import { runtimeConfig } from '../config/runtimeConfig';
import { authAPI } from '../services/api';

// Get environment configuration from runtime config
const API_BASE_URL = runtimeConfig.REACT_APP_API_URL;

export class TokenManager {
  private static checkInterval: NodeJS.Timeout | null = null;
  private static readonly CHECK_INTERVAL_MS = 60000; // Check every minute
  private static readonly REFRESH_BUFFER_MS = 5 * 60 * 1000;

  static startTokenMonitoring() {
    // Clear any existing interval
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }

    // Check token validity immediately
    this.checkTokenValidity();

    // Set up periodic checks
    this.checkInterval = setInterval(() => {
      this.checkTokenValidity();
    }, this.CHECK_INTERVAL_MS);
  }

  static stopTokenMonitoring() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  private static async checkTokenValidity() {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        this.handleLogout();
        return;
      }

      const expiresAtRaw = localStorage.getItem('token_expires_at');
      if (expiresAtRaw) {
        const expiresAt = new Date(expiresAtRaw).getTime();
        const msUntilExpiry = expiresAt - Date.now();
        if (msUntilExpiry > 0 && msUntilExpiry < this.REFRESH_BUFFER_MS) {
          console.log('Token expiring soon, refreshing...');
          await authAPI.refreshToken();
          return;
        }
        if (msUntilExpiry <= 0) {
          console.log('Token expired, logging out...');
          this.handleLogout();
          return;
        }
      }

      // Fallback validation when expiry metadata is missing
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        console.log('Token validation failed, logging out...');
        this.handleLogout();
      }
    } catch (error) {
      console.log('Token validation failed, logging out...');
      this.handleLogout();
    }
  }

  private static handleLogout() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('token_expires_at');
    // Only redirect if we're not already on the login page
    if (window.location.pathname !== '/') {
      window.location.href = '/';
    }
  }

  static isTokenExpired(): boolean {
    const expiresAt = localStorage.getItem('token_expires_at');
    if (expiresAt) {
      return new Date(expiresAt) <= new Date();
    }
    return !localStorage.getItem('authToken');
  }
}
